/**
 * Confirmation dialog for a `useBlocker` guard, shared by the programme
 * editor and the module workspace so unsaved-change navigation is confirmed
 * identically everywhere: link clicks, programmatic navigation, and browser
 * Back/Forward all funnel through the same blocker and the same dialog.
 *
 * @module router/NavigationBlockerDialog
 */

import type { Blocker } from "react-router";

import { Button, Modal, Spinner } from "@/components/ui";

export function NavigationBlockerDialog({
  blocker,
  itemLabel,
  settling = false,
  onLeave = () => undefined,
}: {
  blocker: Blocker;
  itemLabel: string;
  /** The reader chose to leave what is unsaved behind. */
  onLeave?: () => void;
  /** The held move is waiting on a save, and goes ahead by itself once it lands. */
  settling?: boolean;
}) {
  if (blocker.state !== "blocked") {
    return null;
  }
  const stay = () => blocker.reset?.();
  const stayButton = (
    <Button variant="outline" data-testid="navigation-blocker-stay" onClick={stay}>
      Stay
    </Button>
  );
  const leaveButton = (
    <Button
      variant="primary"
      tone="danger"
      icon="sign-out"
      data-testid="navigation-blocker-leave"
      onClick={() => {
        onLeave();
        blocker.proceed?.();
      }}
    >
      Leave anyway
    </Button>
  );
  if (settling) {
    return (
      <Modal
        open
        onClose={stay}
        size="sm"
        describe
        title="Saving changes"
        data-testid="navigation-blocker-saving"
        // Leaving stays open: a save that never answers cannot hold the reader.
        footer={
          <>
            {stayButton}
            {leaveButton}
          </>
        }
      >
        <span className="flex items-center gap-2">
          <Spinner size="sm" />
          Saving this {itemLabel} before leaving.
        </span>
      </Modal>
    );
  }
  return (
    <Modal
      open
      // Escape and the backdrop keep the work, as Stay does.
      onClose={stay}
      tone="danger"
      size="sm"
      describe
      title="Unsaved changes"
      data-testid="navigation-blocker-dialog"
      footer={
        <>
          {stayButton}
          {leaveButton}
        </>
      }
    >
      This {itemLabel} has changes that could not be saved. Leaving now loses them.
    </Modal>
  );
}
